import React, { useState } from 'react';
import { BudgetAggregateRow } from '../../config';
import AggregateRow from './AggregateRow';
import RwdClientsSettingsModal from './RwdClientsSettingsModal';
import './aggregate-section.scss';

interface AggregateSectionProps {
  rows: BudgetAggregateRow[];
  fyStartYear: number;
  /** Optimistic local update when a target cell is saved — no refetch. */
  onTargetSaved: (rowId: number, year: number, month: number, newAmount: number) => void;
  /** Optimistic local update when the Total Fee cell is saved — no refetch. */
  onFeeSaved: (rowId: number, newAmount: number) => void;
  /** Full refetch — used after add/remove since the row list changes. */
  onListChanged: () => void;
  /** Optimistic local reorder — no refetch (avoids scroll-reset). */
  onLocalReorder: (orderedIds: number[]) => void;
  /** FY25/26 view has an extra Pre-FY column — aggregate rows render a
   * placeholder so the grid stays aligned. */
  showPriorColumn?: boolean;
}

/**
 * Render aggregate budget rows under the per-project rows:
 *   - RWC row (always present, auto-created server-side)
 *   - One row per chosen RWD client, ordered by sort_order
 *   - A settings cog on the RWD heading opens a modal to add / reorder /
 *     remove RWD clients. Kept out of the table to reduce accidental clicks.
 */
const AggregateSection: React.FC<AggregateSectionProps> = ({
  rows,
  fyStartYear,
  onTargetSaved,
  onFeeSaved,
  onListChanged,
  onLocalReorder,
  showPriorColumn = false,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const rwcRow = rows.find((r) => r.row_type === 'rwc_all') ?? null;
  const rwdRows = rows.filter((r) => r.row_type === 'rwd_client');

  return (
    <>
      <div className="aggregate-section__heading">
        <span>RWC</span>
      </div>
      {rwcRow && (
        <AggregateRow
          row={rwcRow}
          fyStartYear={fyStartYear}
          onTargetSaved={onTargetSaved}
          onFeeSaved={onFeeSaved}
          showPriorColumn={showPriorColumn}
        />
      )}

      <div className="aggregate-section__heading aggregate-section__heading--with-action">
        <span>
          RWD by client
          <button
            type="button"
            className="aggregate-section__settings-btn"
            onClick={() => setSettingsOpen(true)}
            title="Manage RWD client rows"
            aria-label="Manage RWD client rows"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </span>
      </div>
      {rwdRows.map((row) => (
        <AggregateRow
          key={row.id}
          row={row}
          fyStartYear={fyStartYear}
          onTargetSaved={onTargetSaved}
          onFeeSaved={onFeeSaved}
          showPriorColumn={showPriorColumn}
        />
      ))}

      <RwdClientsSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        rwdRows={rwdRows}
        onListChanged={onListChanged}
        onLocalReorder={onLocalReorder}
      />
    </>
  );
};

export default AggregateSection;
